<script>
   /*************************************************************/
   /* AppContainer for new data.                                */
   /* -------------------------                                 */
   /* app container for applying NIMCA model to new set         */
   /*************************************************************/

   import { Container, Button } from 'svelte-controls-basic';
   import { downloadResults, downloadModel, NIMCA } from '../utils.svelte';

   import { excludeOutliers } from '@mdatools-apps/shared/utils';
   import { applyPrep } from '@mdatools-apps/shared/methods/prep.js';
   import Outliers from '@mdatools-apps/shared/widgets/Outliers.svelte';
   import DataLoader from '@mdatools-apps/shared/components/pca/DataLoader.svelte';
   import ModelLoader from '../shared/ModelLoader.svelte';

   import ModelTable from '../shared/ModelTable.svelte';
   import NIMCASettings from '../shared/NIMCASettings.svelte';

   import ResultTable from './ResultTable.svelte';
   import AppPlots from './AppPlots.svelte';

   // WASM promise and session object from parent app
   let {WASM, backupSession, session = $bindable()} = $props();

   // container for possible errors from data loader and preprocessing
   let preperr = $state('');

   // generation counter for the preprocessing runs - see the note in 'prepApply' below
   let run = 0;

   // get part of the session for this tab
   const s = $derived(session['new']);

   // if NIMCA model is not instance of NIMCA, recreate it
   $effect(() => {
      if (s.nimca && !(s.nimca instanceof NIMCA)) {
         s.nimca = NIMCA.fromJSON(s.nimca);
      }
   });



   // callback function to set object with a give position in plot series
   // as outlier
   function markOutlier(pos) {

      // check that position is valid
      if (pos < 0 || pos > s.newres.nrows - 1) return;

      // find sample ID corresponding to the position
      // this is needed if some of the objects already
      // removed from results as outliers
      const id = s.newseto.objindices[pos];

      // add the object to outliers array and reset the model and all related outcomes
      if (id >= 0 && !s.outliers.includes(id)) {
         s.outliers.push(id);
         s.newseto = excludeOutliers(s.newset, s.outliers);
         s.newsetp = null;
         s.newres = null;
      }
   }

   // callback to reset outliers
   function resetOutliers() {
      s.outliers = [];
      s.newseto = s.newset;
      s.newsetp = null;
      s.newres = null;
   }

   // callback to undo outliers
   function undoOutliers() {
      s.outliers.pop();
      s.newseto = excludeOutliers(s.newset, s.outliers);
      s.newsetp = null;
      s.newres = null;
   }


   async function prepApply(data, prep) {

      // only the newest run may write - 'WASM()' builds a fresh module per call, so two runs in
      // flight are unrelated promises with no ordering between them. plain 'let', never '$state'
      // (it is written from inside the effect below) and never module scope
      const myRun = ++run;

      preperr = '';
      if (!data) return null;
      if (!prep || Object.keys(prep).length === 0) {
         s.newsetp = data;
         return;
      }
      WASM()
         .then(Module => {
            if (myRun !== run) return;
            // the snapshot leaves the matrix out: it cloned it in full for 'applyPrep' to drop
            const { values, ...meta } = data;
            s.newsetp = applyPrep(Module, { values, ...$state.snapshot(meta) }, prep);
         })
         .catch(e =>{
            console.error(e)
            if (myRun !== run) return;
            s.newsetp = null;
            preperr = e instanceof Error ? e.message : String(e);
         });
   };

   // pre-rendering effect to avoid bad structure of the session object
   $effect.pre(() => {

      // if new set is set null - reset results and all related parameters
      if (s.newset === null) {
         preperr = '';
         s.outliers = [];
         s.newsetp = null;
         s.newseto = null;
         s.newres = null;
      }

      if (s.nimca === null) {
         preperr = '';
         s.outliers = [];
         s.newset = null;
         s.newsetp = null;
         s.newseto = null;
         s.newres = null;
      }

   });

   // if model object is set to null - reset dependent objects
   $effect(() => {
      if (s.nimca === null) {
         preperr = '';
         s.outliers = [];
         s.newset = null;
         s.newsetp = null;
         s.newseto = null;
         s.newres = null;
      }
   });

   // if new set is set null - reset results and all related parameters
   $effect(() => {
      if (s.newset === null) {
         preperr = '';
         s.outliers = [];
         s.newsetp = null;
         s.newseto = null;
         s.newres = null;
      }
   });

   // 'nimca.ncols' counts the columns the model was fitted on, which is after preprocessing. a
   // chain containing 'Trim tails' drops columns, so comparing a raw file against it rejected the
   // very file the model was built for. the prep model records the count it expects as 'npred'.
   // the loader's width guess reads it as well
   const npred = $derived(!s.nimca ? null :
      s.prep && Object.keys(s.prep).length > 0 ? s.prep.npred : s.nimca.ncols);

   $effect(() => {
      if (s.newset && !s.newseto) {
         preperr = '';
         if (s.newset.values.ncols !== npred) {
            preperr = `Wrong number of predictors (expected ${npred}, got ${s.newset.values.ncols}), check settings and try to load again.`;
            return;
         }
         s.newseto = s.newset;
      }
   });

   $effect(() => {
      if (s.newseto && !s.newsetp) {
         preperr = '';
         prepApply(s.newseto, s.prep);
      }

      // the session outlives this tab, so a run still in flight would write into it after the
      // condition above stopped holding or the tab unmounted. this is the only effect which owns
      // the counter, so the teardown can not cancel a run launched in the same flush
      return () => { run++; };
   });

   $effect(() => {
      if (s.newsetp && s.nimca) {
         try {
            s.newres = s.nimca.predict(s.newsetp, s.outliers);
            preperr = '';
         } catch (e) {
            // a stale result must not survive a failed prediction - it would stay on screen and
            // stay downloadable while describing data it was not computed from
            s.newres = null;
            preperr = e instanceof Error ? e.message : String(e);
         }
      }
   });

   // reactive effect for backing up the session. the session is read by 'backupSession' when the
   // write is due, not here, and the first run is skipped: it only ever coincides with a session
   // the app was given as a whole, which is already on disk, is written by 'MainApp' itself, or is
   // a fresh one that need not be stored. the values after the function are the dependencies (see
   // 'backupSession'). alpha, gamma and the limits type are state of the model, and without these
   // reads a change to any of them was never written to the database
   let mounted = false;
   $effect(() => {
      const deps = [s.newset, s.newres, s.nimca, s.nimca?.alpha, s.nimca?.gamma, s.nimca?.limType];
      if (mounted) backupSession(() => session, ...deps);
      mounted = true;
   });
</script>


<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="model-loader">
         <ModelLoader bind:nimca={s.nimca} bind:prep={s.prep} />
      </div>

      {#if s.nimca}
      <div class="data-loader">
         <DataLoader
            bind:dataset={s.newset}
            ncols={npred}
            className="Class"
            classMandatory={false}
            csvOnly={false}
         />
         <!-- error message -->
         {#if preperr}
         <Container status="error">
            <p>{preperr}</p>
         </Container>
         {/if}
      </div>
      {/if}

      {#if s.outliers && s.outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers outliers={s.outliers} dataset={s.newset} {resetOutliers} {undoOutliers} />
      </div>
      {/if}

      <div class="nimca-settings">

         {#if s.nimca && s.newsetp}
         <NIMCASettings
            bind:nimca={s.nimca}
            isAlt={true}
            fit={false}
         />
         {/if}

         {#if s.nimca && s.newres}
         <Container label='Download'>
            <div class="buttons">
            <Button onclick={() => downloadResults(s.nimca, s.newres, s.newset, "new")} text="Res (new)"/>
            <Button onclick={() => downloadModel(s.nimca, s.prep)} text="Model" />
            </div>
         </Container>
         {/if}
      </div>

      {#if s.nimca && s.newres}
      <div class="model-stats">
         <ModelTable nimca={s.nimca} />
      </div>
      <div class="result-stats">
         <ResultTable res={s.newres} />
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !s.nimca || !s.newres}
         <div class="help-text">
         <p>
            In this tab you can apply previously trained NIMCA model to a new dataset with objects whose class is unknown (hence the app
            does not expect column with target classes).
         </p>
         {#if !s.nimca}
            <p><strong>Step 1</strong></p>
            <p>
               Load previously trained NIMCA model from file.
            </p>
         {:else}
            {#if !s.newset}
            <p><strong>Step 2</strong></p>
            <p>
               Load data set from CSV file (or SPC files). The requirements to the file are the same as for the
               training set but no columns with reference classes is expected. If you have reference classes, use <em>Test</em> tab.
            </p>
            {/if}
         {/if}         </div>
      {:else if s.nimca && s.newres }
         {#key s.newres }
         <AppPlots {markOutlier} {...s} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      border-radius: 0.25rem;
   }

   .app-controls > .model-stats,
   .app-controls > .result-stats {
      background: #e8e8e8;
   }

   .app-controls > .model-stats,
   .app-controls > .result-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }


   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }



   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }



   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(325px, 30%) 1fr;
      }
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

</style>